/**
 * Authorization vocabulary — the refusal tokens the server-side role check puts in a log
 * line and in an API response's `details.roleReason`.
 *
 * Why its own module rather than a block of constants inside the middleware: the
 * middleware builds a DynamoDB client at import, so anything that merely wants to NAME a
 * refusal — a schema, a handler's error mapper, a test — would have to pay for a client to
 * read three strings. Vocabularies therefore live in dependency-free modules of their own,
 * exactly as `outcome-vocabulary`, `decision-vocabulary`, `action-classes` and
 * `knowledge-vocabulary` already do.
 *
 * And why not in `knowledge-vocabulary`, where these three tokens started: they are not
 * Knowledge-surface vocabulary. `assertWorkspaceRole` is the platform's one server-side
 * role check, now shared by Knowledge erasure and MAR-170's workspace update boundary. A
 * workspaces route with nothing to do with Knowledge must not require
 * `@marva/shared/knowledge-vocabulary` merely to read a role refusal.
 *
 * @module auth-vocabulary
 */

'use strict';

/**
 * Finer-grained refusal codes for the workspace role check, carried in
 * `details.roleReason` and in the `AUTH_FAILURE` log line.
 *
 * These are diagnostics, not the surface contract — a web app branches on the surface's
 * own `details.reason`, which is one token for every role refusal. The distinction these
 * three keep is the one the middleware is built around: `no_workspace_membership` and
 * `insufficient_workspace_role` are FACTS about the caller, `role_check_failed` means we
 * could not find out. Collapsing the third into either of the first two would let a
 * DynamoDB blip be filed as a membership verdict.
 */
const ROLE_REASONS = Object.freeze({
  NO_WORKSPACE_MEMBERSHIP: 'no_workspace_membership',
  INSUFFICIENT_WORKSPACE_ROLE: 'insufficient_workspace_role',
  ROLE_CHECK_FAILED: 'role_check_failed',
  // The ACCOUNT-level analogues (MAR-237), for an act whose target workspace does not exist
  // yet — creating one. `no_account_role`: the caller holds nothing in the account (not its
  // owner, no row in any of its workspaces) or is a credential with no person behind it.
  // `insufficient_account_role`: they hold rows there, but none that permits the act (a
  // `member` everywhere). `role_check_failed` keeps its meaning: we could not find out.
  NO_ACCOUNT_ROLE: 'no_account_role',
  INSUFFICIENT_ACCOUNT_ROLE: 'insufficient_account_role'
});

module.exports = {
  ROLE_REASONS
};
